import { queryOptions } from "@tanstack/react-query";
import { getFyndanuListings, getFyndanuListing } from "@/lib/fyndanu.functions";
import { getBlocketVehicles, type BlocketVehicleCard } from "@/lib/blocket.functions";

export const fyndanuListingsQuery = queryOptions({
  queryKey: ["fyndanu-listings"],
  queryFn: () => getFyndanuListings(),
  staleTime: 5 * 60 * 1000,
});

export const blocketVehiclesQuery = queryOptions({
  queryKey: ["blocket-vehicles"],
  queryFn: () => getBlocketVehicles(),
  staleTime: 5 * 60 * 1000,
});

export type { BlocketVehicleCard };

export const fyndanuListingQuery = (id: string) =>
  queryOptions({
    queryKey: ["fyndanu-listing", id],
    queryFn: () => getFyndanuListing({ data: { id } }),
    staleTime: 5 * 60 * 1000,
  });

export function formatPrice(p: number | null): string {
  if (p == null) return "Pris på förfrågan";
  return new Intl.NumberFormat("sv-SE").format(p) + " kr";
}

export function formatMil(mil: number | null): string | null {
  if (mil == null) return null;
  return new Intl.NumberFormat("sv-SE").format(mil) + " mil";
}

/** Körkortsklass baserat på totalvikt */
export function licenseClass(totalWeight: number | null): "B" | "B96/BE" | "C1" | null {
  if (totalWeight == null) return null;
  if (totalWeight <= 3500) return "B";
  if (totalWeight <= 4250) return "B96/BE";
  return "C1";
}

/** Enkel månadskostnad — annuitet, 84 mån, 6,95% (ändra i UI) */
export function monthlyPayment(
  price: number,
  downPayment: number,
  months: number,
  annualRate: number,
): number {
  const principal = Math.max(0, price - downPayment);
  const r = annualRate / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
}

/** Normaliserar en annonstitel så samma fordon känns igen mellan källor. */
function vehicleKey(title: string | null | undefined): string {
  return (title ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\b(husbil|husvagn|till salu|s(a|å)ld|ny|begagnad)\b/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Tar bort Blocket-annonser som redan finns i Fyndanu-lagret.
 * Matchar på normaliserad titel (och pris när båda finns) så samma
 * fordon inte visas två gånger.
 */
export function dedupeBlocketAgainstFyndanu<
  F extends { title?: string | null; price?: number | null },
  B extends { title: string | null; price: number | null },
>(fyndanu: F[], blocket: B[]): B[] {
  const keys = new Set(fyndanu.map((f) => vehicleKey(f.title)).filter(Boolean));
  const words = (k: string) => k.split(" ").filter((w) => w.length > 2);
  return blocket.filter((b) => {
    const key = vehicleKey(b.title);
    if (!key) return true;
    if (keys.has(key)) return false;
    // Delvis match: minst 2 gemensamma ord (märke + modell) räknas som samma fordon
    const bw = words(key);
    for (const k of keys) {
      const kw = new Set(words(k));
      const shared = bw.filter((w) => kw.has(w)).length;
      if (shared >= 2) return false;
    }
    return true;
  });
}
